import { useState, useEffect } from 'react'
import { Copy, Check } from 'lucide-react'
import { Button } from '@/ui/atoms/button/Button'
import { cn } from '@/ui/utils/style'

interface CopyButtonProps {
  text: string
  className?: string
  iconSize?: number
  showText?: boolean
  successMessage?: string
}

export function CopyButton({
  text,
  className,
  iconSize = 16,
  showText = false,
  successMessage = 'Copied!',
}: CopyButtonProps) {
  const [isCopied, setIsCopied] = useState(false)

  async function copyToClipboard() {
    try {
      await navigator.clipboard.writeText(text)
      setIsCopied(true)
    } catch (error) {
      console.error('Failed to copy text:', error)
    }
  }

  useEffect(() => {
    if (isCopied) {
      const timer = setTimeout(() => {
        setIsCopied(false)
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [isCopied])

  if (isCopied && showText) {
    return <span className={cn('animate-fade-in text-primary text-sm', className)}>{successMessage}</span>
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={copyToClipboard}
      className={cn('h-auto p-1 text-muted-foreground transition-colors hover:text-secondary-foreground', className)}
      aria-label={`Copy ${text}`}
    >
      {isCopied ? <Check size={iconSize} className="text-green-500" /> : <Copy size={iconSize} />}
      {showText && !isCopied && <span className="ml-1 text-xs">Copy</span>}
    </Button>
  )
}
